import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { EMPTY_PREFS, type NavPreferences } from "@/lib/nav-prefs";

/**
 * Per-user, per-organization navigation preferences (`user_nav_preferences`).
 * RLS restricts every row to its owner; this hook only stores ordering and DAR
 * placement — never authorization state.
 */

type Key = string;
const cache = new Map<Key, NavPreferences>();
const listeners = new Set<(key: Key) => void>();

function keyFor(userId: string, orgId: string): Key {
  return `${userId}:${orgId}`;
}

function emit(key: Key) {
  listeners.forEach((l) => l(key));
}

export type NavPreferencesState = {
  prefs: NavPreferences;
  loading: boolean;
  /** Persist a partial update for the active user + organization. */
  save: (patch: Partial<NavPreferences>) => Promise<void>;
  ready: boolean;
};

export function useNavPreferences(): NavPreferencesState {
  const { user, currentOrg } = useAuth();
  const userId = user?.id ?? null;
  const orgId = currentOrg?.id ?? null;
  const key = userId && orgId ? keyFor(userId, orgId) : null;

  const [prefs, setPrefs] = useState<NavPreferences>(() => (key ? cache.get(key) ?? EMPTY_PREFS : EMPTY_PREFS));
  const [loading, setLoading] = useState(!!key && !(key && cache.has(key)));

  // Re-read from the shared cache when another surface updates it.
  useEffect(() => {
    const l = (changed: Key) => {
      if (changed === key) setPrefs(cache.get(changed) ?? EMPTY_PREFS);
    };
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, [key]);

  // Load on user/org change. Organization switches never reuse another org's row.
  useEffect(() => {
    if (!userId || !orgId || !key) {
      setPrefs(EMPTY_PREFS);
      setLoading(false);
      return;
    }
    const cached = cache.get(key);
    if (cached) { setPrefs(cached); setLoading(false); }
    let cancelled = false;
    (async () => {
      setLoading(!cached);
      const { data } = await supabase
        .from("user_nav_preferences")
        .select("bottom_nav_order, sidebar_order, dar_orb_placement")
        .eq("user_id", userId)
        .eq("organization_id", orgId)
        .maybeSingle();
      if (cancelled) return;
      const next: NavPreferences = data
        ? {
            bottom_nav_order: (data.bottom_nav_order ?? []) as string[],
            sidebar_order: (data.sidebar_order ?? []) as string[],
            dar_orb_placement: (data.dar_orb_placement === "nav_bar" ? "nav_bar" : "floating"),
          }
        : EMPTY_PREFS;
      cache.set(key, next);
      setPrefs(next);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userId, orgId, key]);

  const save = useCallback(
    async (patch: Partial<NavPreferences>) => {
      if (!userId || !orgId || !key) return;
      const next: NavPreferences = { ...(cache.get(key) ?? EMPTY_PREFS), ...patch };
      cache.set(key, next);
      emit(key);
      await supabase.from("user_nav_preferences").upsert(
        {
          user_id: userId,
          organization_id: orgId,
          bottom_nav_order: next.bottom_nav_order,
          sidebar_order: next.sidebar_order,
          dar_orb_placement: next.dar_orb_placement,
        },
        { onConflict: "user_id,organization_id" },
      );
    },
    [userId, orgId, key],
  );

  return { prefs, loading, save, ready: !!key && !loading };
}
